import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  GraduationCap, PlayCircle, FileText, Certificate, Sparkle, Plus, Trash,
  CheckCircle, Warning, ChartBar, Clock, BookOpen, Question, DownloadSimple,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { code: "soft_skills", label: "Soft Skills" },
  { code: "technical", label: "Technical" },
  { code: "compliance", label: "Compliance" },
  { code: "onboarding", label: "Onboarding" },
  { code: "leadership", label: "Leadership" },
  { code: "product", label: "Product" },
];

const CAT_TONE = {
  soft_skills: "bg-zenith-teal/10 text-zenith-teal-700 border-zenith-teal/30",
  technical: "bg-zenith-blue-500/10 text-zenith-blue-700 border-zenith-blue-500/30",
  compliance: "bg-zenith-magenta/10 text-zenith-magenta border-zenith-magenta/30",
  onboarding: "bg-zenith-green/10 text-zenith-green border-zenith-green/30",
  leadership: "bg-amber-50 text-amber-700 border-amber-200",
  product: "bg-zinc-100 text-zinc-700 border-zinc-200",
};


function CoursePlayerDialog({ enrollment, onCompleted }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [currentMod, setCurrentMod] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/lms/courses/${enrollment.course_id}`);
      setDetail(data);
      setCurrentMod(data.modules?.[0] || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const complete = async (mod) => {
    setBusy(true);
    try {
      await api.post(`/lms/enrollments/${enrollment.id}/modules/${mod.id}/complete`);
      toast.success(`"${mod.title}" marked complete`);
      onCompleted();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const submitQuiz = async () => {
    if (!currentMod?.quiz) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lms/enrollments/${enrollment.id}/modules/${currentMod.id}/attempt`, { answers });
      if (data.passed) { toast.success(`Passed! Score: ${data.score_percent}%`); onCompleted(); }
      else toast.error(`Failed — ${data.score_percent}%. Need ${data.pass_percent}%`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const contentUrl = currentMod?.content_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/lms/modules/${currentMod.id}/content`
    : currentMod?.content_url;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`course-open-${enrollment.id}`} size="sm" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">
          <PlayCircle size={14} className="mr-1" /> {enrollment.progress_percent > 0 ? "Resume" : "Start"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <GraduationCap size={16} weight="duotone" className="text-zenith-teal" />
            {detail?.title || "Loading…"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 flex gap-3 min-h-0">
          <aside className="w-56 border border-zinc-200 rounded-sm bg-zinc-50 overflow-y-auto">
            {(detail?.modules || []).map((m, i) => (
              <button
                key={m.id}
                onClick={() => { setCurrentMod(m); setAnswers([]); }}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 text-xs hover:bg-white ${currentMod?.id === m.id ? "bg-white border-l-2 border-l-zenith-magenta font-semibold" : ""}`}
              >
                <div className="flex items-center gap-2"><span className="text-zinc-400 font-mono-plex">{i + 1}.</span> {m.title}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{m.content_type} · {m.duration_min}m</div>
              </button>
            ))}
          </aside>
          <div className="flex-1 overflow-y-auto border border-zinc-200 rounded-sm p-4">
            {currentMod ? (
              <>
                <h3 className="font-display text-lg font-bold text-adb-navy">{currentMod.title}</h3>
                <div className="mt-4">
                  {currentMod.content_type === "video" && contentUrl && (
                    <video src={contentUrl} controls className="w-full rounded-sm" />
                  )}
                  {currentMod.content_type === "pdf" && contentUrl && (
                    <iframe src={contentUrl} className="w-full h-96 rounded-sm border border-zinc-200" title="PDF" />
                  )}
                  {currentMod.content_type === "text" && (
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">{currentMod.body_text}</div>
                  )}
                  {currentMod.content_type === "quiz" && currentMod.quiz && (
                    <div className="space-y-4">
                      <div className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Quiz · pass {currentMod.quiz.pass_percent}%</div>
                      {currentMod.quiz.questions.map((q, qi) => (
                        <div key={qi} className="border border-zinc-200 rounded-sm p-3">
                          <div className="text-sm font-medium">{qi + 1}. {q.q}</div>
                          <div className="mt-2 space-y-1">
                            {q.options.map((opt, oi) => (
                              <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="radio" name={`q-${qi}`} checked={answers[qi] === oi}
                                       onChange={() => { const a = [...answers]; a[qi] = oi; setAnswers(a); }} />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      <Button onClick={submitQuiz} disabled={busy || answers.length !== currentMod.quiz.questions.length}
                              data-testid={`quiz-submit-${currentMod.id}`}
                              className="rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90">
                        Submit quiz
                      </Button>
                    </div>
                  )}
                  {currentMod.content_type !== "quiz" && (
                    <Button onClick={() => complete(currentMod)} disabled={busy} className="mt-4 rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
                      <CheckCircle size={14} className="mr-1" /> Mark complete
                    </Button>
                  )}
                </div>
              </>
            ) : <div className="text-sm text-zinc-500">Pick a module to begin.</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function AuthorCourseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "soft_skills", is_mandatory: false, retrain_months: "" });
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/lms/courses", { ...form, retrain_months: parseInt(form.retrain_months) || null });
      toast.success("Course created"); setOpen(false); onCreated();
      setForm({ title: "", description: "", category: "soft_skills", is_mandatory: false, retrain_months: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="course-new-btn" className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
          <Plus size={14} className="mr-1" /> New course
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">New course</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title *</Label>
            <Input data-testid="course-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_mandatory} onCheckedChange={c => setForm({ ...form, is_mandatory: c })} data-testid="course-form-mandatory" />
            <Label className="text-xs">Mandatory (compliance)</Label>
          </div>
          {form.is_mandatory && <div><Label className="text-xs">Re-train every (months)</Label>
            <Input data-testid="course-form-retrain" type="number" value={form.retrain_months} onChange={e => setForm({ ...form, retrain_months: e.target.value })} className="rounded-sm mt-1" placeholder="12" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="course-form-submit" onClick={submit} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ModuleUploadDialog({ course, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content_type: "text", body_text: "", external_url: "", order: 1, duration_min: 5 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") fd.append(k, v); });
      if (file) fd.append("file", file);
      await api.post(`/lms/courses/${course.id}/modules`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Module added"); setOpen(false); onAdded();
      setForm({ title: "", content_type: "text", body_text: "", external_url: "", order: (course.module_count || 0) + 2, duration_min: 5 });
      setFile(null);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid={`add-module-${course.id}`}><Plus size={12} className="mr-1" /> Add module</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Add a module</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title *</Label>
            <Input data-testid="module-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.content_type} onValueChange={v => setForm({ ...form, content_type: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["video", "pdf", "text", "quiz", "scorm"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Order</Label>
              <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 1 })} className="rounded-sm mt-1 font-mono-plex" /></div>
            <div><Label className="text-xs">Duration (m)</Label>
              <Input type="number" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })} className="rounded-sm mt-1 font-mono-plex" /></div>
          </div>
          {form.content_type === "text" ? (
            <div><Label className="text-xs">Body</Label>
              <Textarea rows={4} value={form.body_text} onChange={e => setForm({ ...form, body_text: e.target.value })} className="rounded-sm mt-1" /></div>
          ) : (
            <>
              <div><Label className="text-xs">Upload file (max 300 MB)</Label>
                <input data-testid="module-form-file" type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs" /></div>
              <div><Label className="text-xs">or external URL (YouTube, Vimeo)</Label>
                <Input value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} className="rounded-sm mt-1 font-mono-plex" placeholder="https://…" /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="module-form-submit" onClick={submit} disabled={busy} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">{busy ? "Uploading…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AIQuizDialog({ module, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lms/ai/quiz-from-text", { text, n_questions: 5, difficulty: "medium" });
      await api.post(`/lms/modules/${module.id}/quiz`, { questions: data.questions, pass_percent: 70, attempts_allowed: 3 });
      toast.success(`Quiz attached: ${data.questions.length} questions`);
      setOpen(false); onGenerated();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-6 text-[10px]" data-testid={`ai-quiz-${module.id}`}>
          <Sparkle size={10} className="mr-1 text-zenith-magenta" /> AI quiz
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-zenith-magenta" /> Generate a quiz from your content</DialogTitle></DialogHeader>
        <Textarea rows={8} value={text} onChange={e => setText(e.target.value)} placeholder="Paste the training material text or a summary…" className="rounded-sm" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={gen} disabled={busy || !text.trim()} className="rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90 text-white">
            {busy ? "Generating…" : "Generate 5 questions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Learning() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const [enrolments, setEnrolments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [report, setReport] = useState(null);
  const [gaps, setGaps] = useState(null);

  const load = async () => {
    try {
      const [m, c, cx] = await Promise.all([
        api.get("/lms/mine").catch(() => ({ data: [] })),
        api.get("/lms/certificates/mine").catch(() => ({ data: [] })),
        api.get("/lms/courses").catch(() => ({ data: [] })),
      ]);
      setEnrolments(m.data); setCerts(c.data); setCourses(cx.data);
      if (isAdmin) {
        const [r, g] = await Promise.all([
          api.get("/lms/reports/adoption").catch(() => ({ data: null })),
          api.get("/lms/reports/compliance-gaps").catch(() => ({ data: null })),
        ]);
        setReport(r.data); setGaps(g.data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const expandCourse = async (course) => {
    if (expanded === course.id) { setExpanded(null); return; }
    setExpanded(course.id);
    try {
      const { data } = await api.get(`/lms/courses/${course.id}`);
      setExpandedDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async (c, v) => {
    try { await api.patch(`/lms/courses/${c.id}`, { is_published: v }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await api.delete(`/lms/courses/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const enrolMe = async (c) => {
    try { await api.post("/lms/enroll", { course_id: c.id }); toast.success("Enrolled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const enrolments_open = enrolments.filter((e) => e.status !== "completed");
  const enrolments_done = enrolments.filter((e) => e.status === "completed");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Learning · LMS</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Learn. Grow. Certify.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Take assigned courses, browse the catalogue, earn certificates. Admins can author video/PDF/quiz modules and roll out compliance training with automatic re-training cycles.</p>
        </div>
        {isAdmin && <AuthorCourseDialog onCreated={load} />}
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="mine" className="rounded-sm" data-testid="tab-my-learning"><BookOpen size={12} className="mr-1" /> My learning
            {enrolments_open.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{enrolments_open.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="catalog" className="rounded-sm" data-testid="tab-catalog"><GraduationCap size={12} className="mr-1" /> Catalogue</TabsTrigger>
          <TabsTrigger value="certs" className="rounded-sm" data-testid="tab-certs"><Certificate size={12} className="mr-1" /> Certificates
            {certs.length > 0 && <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] rounded-sm">{certs.length}</Badge>}
          </TabsTrigger>
          {isAdmin && <TabsTrigger value="admin" className="rounded-sm" data-testid="tab-admin"><FileText size={12} className="mr-1" /> Author</TabsTrigger>}
          {isAdmin && <TabsTrigger value="reports" className="rounded-sm" data-testid="tab-reports"><ChartBar size={12} className="mr-1" /> Reports</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine" className="mt-4 space-y-3">
          {enrolments_open.length === 0 && (
            <div className="border border-dashed border-zinc-200 bg-zinc-50 rounded-sm p-10 text-center text-sm text-zinc-500">
              No active courses. Browse the catalogue to enrol.
            </div>
          )}
          {enrolments_open.map((e) => (
            <div key={e.id} data-testid={`enrol-row-${e.id}`} className="border border-zinc-200 rounded-sm bg-white p-4 flex items-center gap-4 flex-wrap">
              <div className={`w-12 h-12 rounded-sm grid place-items-center ${CAT_TONE[e.course?.category] || "bg-zinc-100"}`}>
                <GraduationCap size={22} weight="duotone" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="font-display font-bold text-adb-navy">{e.course?.title}</div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{e.course?.category} · {e.course?.duration_min}m</div>
                <div className="mt-2">
                  <Progress value={e.progress_percent} className="h-1.5" />
                  <div className="text-[10px] font-mono-plex mt-1 text-zinc-500">{e.progress_percent}% complete{e.due_date ? ` · due ${e.due_date}` : ""}</div>
                </div>
              </div>
              <CoursePlayerDialog enrollment={e} onCompleted={load} />
            </div>
          ))}
          {enrolments_done.length > 0 && (
            <div className="pt-4 mt-4 border-t border-zinc-100">
              <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">Completed</div>
              {enrolments_done.map((e) => (
                <div key={e.id} className="border border-zinc-200 rounded-sm bg-emerald-50/40 p-3 flex items-center gap-3 mb-2">
                  <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{e.course?.title}</div>
                    <div className="text-[10px] text-zinc-500">Completed {e.completed_at?.slice(0, 10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="catalog" className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {courses.filter((c) => c.is_published || isAdmin).map((c) => {
            const alreadyEnrolled = enrolments.some((e) => e.course_id === c.id);
            return (
              <div key={c.id} data-testid={`catalog-card-${c.id}`} className="border border-zinc-200 rounded-sm bg-white p-4 flex flex-col">
                <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border w-fit ${CAT_TONE[c.category] || ""}`}>
                  {c.category}{c.is_mandatory && <span className="ml-1 text-red-600">·MANDATORY</span>}
                </div>
                <div className="font-display font-bold text-adb-navy mt-2">{c.title}</div>
                <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{c.description}</div>
                <div className="text-[10px] text-zinc-400 mt-2">{c.module_count || 0} modules · {c.duration_min || 0} min · {c.enrollment_count || 0} enrolled</div>
                <div className="mt-3 flex-1" />
                <div className="flex gap-1 mt-2">
                  {!alreadyEnrolled && user?.employee_id && (
                    <Button size="sm" onClick={() => enrolMe(c)} data-testid={`enrol-btn-${c.id}`} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold h-7 text-xs">Enrol</Button>
                  )}
                  {alreadyEnrolled && <Badge variant="outline" className="rounded-sm text-[10px]">Enrolled</Badge>}
                </div>
              </div>
            );
          })}
          {courses.length === 0 && (
            <div className="col-span-full border border-dashed border-zinc-200 bg-zinc-50 rounded-sm p-10 text-center text-sm text-zinc-500">
              No courses yet. {isAdmin ? "Create one to get started." : "Ask HR to publish some."}
            </div>
          )}
        </TabsContent>

        <TabsContent value="certs" className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {certs.length === 0 && <div className="col-span-full border border-dashed border-zinc-200 bg-zinc-50 rounded-sm p-10 text-center text-sm text-zinc-500">Complete a course to earn your first certificate.</div>}
          {certs.map((cert) => (
            <div key={cert.id} className="border border-zenith-magenta/30 rounded-sm bg-gradient-to-br from-white to-zenith-magenta/5 p-4">
              <Certificate size={22} weight="duotone" className="text-zenith-magenta" />
              <div className="font-display font-bold text-adb-navy mt-2">{cert.course_title}</div>
              <div className="text-[11px] text-zinc-500 mt-1">Issued {cert.issued_at?.slice(0, 10)}</div>
              {cert.expires_at && <div className="text-[11px] text-red-600">Expires {cert.expires_at}</div>}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/lms/certificates/${cert.cert_id}/pdf`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 mt-2 text-xs border border-adb-navy text-adb-navy px-2 py-1 rounded-sm hover:bg-adb-navy hover:text-white">
                <DownloadSimple size={12} /> Download PDF
              </a>
            </div>
          ))}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin" className="mt-4 space-y-2">
            {courses.map((c) => (
              <div key={c.id} className="border border-zinc-200 rounded-sm bg-white">
                <div className="p-3 flex items-center gap-3 flex-wrap">
                  <button onClick={() => expandCourse(c)} className="flex-1 text-left min-w-[200px]">
                    <div className="font-display font-bold text-adb-navy">{c.title}</div>
                    <div className="text-[11px] text-zinc-500">{c.category} · {c.module_count} modules · {c.enrollment_count} enrolled</div>
                  </button>
                  <div className="flex items-center gap-2">
                    <Switch checked={c.is_published} onCheckedChange={(v) => publish(c, v)} data-testid={`publish-${c.id}`} />
                    <span className="text-[11px] text-zinc-500">{c.is_published ? "Published" : "Draft"}</span>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-500 h-7 text-xs" data-testid={`delete-course-${c.id}`} onClick={() => del(c)}><Trash size={12} /></Button>
                </div>
                {expanded === c.id && expandedDetail?.id === c.id && (
                  <div className="border-t border-zinc-100 p-3 bg-zinc-50 space-y-2">
                    {(expandedDetail.modules || []).map((m) => (
                      <div key={m.id} className="border border-zinc-200 bg-white rounded-sm p-2 flex items-center gap-2">
                        <div className="text-xs font-mono-plex text-zinc-400 w-6">{m.order}</div>
                        <div className="flex-1 text-sm">{m.title} <span className="text-[10px] text-zinc-500 ml-1 uppercase tracking-widest">{m.content_type} · {m.duration_min}m</span></div>
                        {m.content_type === "quiz" && <Badge variant="outline" className="rounded-sm text-[10px]">Quiz · {m.quiz?.questions?.length || 0}q</Badge>}
                        {!m.quiz && m.content_type !== "quiz" && <AIQuizDialog module={m} onGenerated={() => expandCourse(c)} />}
                      </div>
                    ))}
                    <ModuleUploadDialog course={c} onAdded={() => expandCourse(c)} />
                  </div>
                )}
              </div>
            ))}
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="reports" className="mt-4 space-y-4">
            {report && (
              <div className="grid md:grid-cols-4 gap-3">
                <MetricCard label="Total courses" value={report.total_courses} icon={GraduationCap} tone="teal" />
                <MetricCard label="Enrolments" value={report.total_enrolments} icon={BookOpen} tone="magenta" />
                <MetricCard label="Completed" value={`${report.completed} (${report.completion_percent}%)`} icon={CheckCircle} tone="green" />
                <MetricCard label="Overdue" value={report.overdue} icon={Warning} tone="blue" />
              </div>
            )}
            {gaps && gaps.gap_count > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-sm p-4">
                <div className="font-display font-bold text-red-700 flex items-center gap-2"><Warning size={16} /> Compliance gaps: {gaps.gap_count}</div>
                <div className="text-xs text-red-600/80 mt-1">Employees who haven't completed a mandatory course.</div>
                <div className="mt-3 max-h-64 overflow-y-auto text-xs">
                  {gaps.gaps.slice(0, 20).map((g, i) => (
                    <div key={i} className="border-b border-red-100 py-1 flex justify-between">
                      <span>{g.employee_name}</span><span className="text-red-500">{g.course_title} · {g.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


const TONE = {
  teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/30", icon: "text-zenith-teal-700" },
  magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/30", icon: "text-zenith-magenta" },
  green: { bg: "bg-zenith-green/10", border: "border-zenith-green/30", icon: "text-zenith-green" },
  blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/30", icon: "text-zenith-blue-700" },
};
function MetricCard({ label, value, icon: Icon, tone }) {
  const t = TONE[tone] || TONE.teal;
  return (
    <div className={`border ${t.border} ${t.bg} rounded-sm p-4 flex items-center gap-3`}>
      <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white ${t.border} border`}>
        <Icon size={18} weight="duotone" className={t.icon} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-display text-xl font-bold text-adb-navy leading-none">{value}</div>
      </div>
    </div>
  );
}
